import React, { useState, useEffect } from 'react';
import { Container, Form, Button, Row, Col, Table } from 'react-bootstrap';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { FaPlus, FaTrash, FaTimes, FaPencilAlt, FaGripVertical, FaEdit } from 'react-icons/fa';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import LoadingSpinner from '../../components/LoadingSpinner';
import categoryService from '../../services/categoryService';
import productService from '../../services/productService';
import { fetchAllCategories, showNotification } from '../../store/actions';
import useTitle from '../../hooks/useTitle';
import defaultProductImage from '../../images/default-product.png';

const AdminCategoryEdit = () => {
    const { data: categories } = useSelector(state => state.categories.all);
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();
    const isNew = id === 'new';

    // Задаем название сайта
    useTitle(isNew ? 'Создание новой категории | Админ-панель' : 'Редактирование категории | Админ-панель');

    // Состояние формы
    const [formData, setFormData] = useState({
        id: null,
        name: '',
        title: '',
        description: '',
        imagePath: '',
        categoryCharacteristics: []
    });

    // Состояние загрузки и ошибок
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [validationErrors, setValidationErrors] = useState({});
    const [imageFile, setImageFile] = useState(null);
    const [imagePreview, setImagePreview] = useState('');
    const [newCharacteristic, setNewCharacteristic] = useState('');

    // Состояния для редактирования характеристики
    const [editingCharacteristicIndex, setEditingCharacteristicIndex] = useState(null);
    const [editingCharacteristicName, setEditingCharacteristicName] = useState('');
    const [isDragging, setIsDragging] = useState(false);
    
    // Состояние для товаров категории
    const [categoryProducts, setCategoryProducts] = useState([]);
    const [productsLoading, setProductsLoading] = useState(false);
    const [hasProductsChanges, setHasProductsChanges] = useState(false);
    
    // Загрузка данных категории если это редактирование
    useEffect(() => {
        if (!isNew && id && !isNaN(id)) {
            fetchCategoryData();
        }
    }, [id, dispatch]);

    // Загрузка товаров категории
    useEffect(() => {
        if (!isNew && formData.name) {
            fetchCategoryProducts();
        }
    }, [formData.name, isNew]);

    const fetchCategoryData = async () => {
        setLoading(true);
        const result = await categoryService.fetchCategoryById(id);

        if (result.success) {
            setFormData({
                id: result.data.id,
                name: result.data.name,
                title: result.data.title,
                description: result.data.description || '',
                imagePath: result.data.imagePath || '',
                categoryCharacteristics: result.data.categoryCharacteristics || []
            });

            if (result.data.imagePath) {
                setImagePreview(result.data.imagePath);
            }
            setLoading(false);
        }
        else {
            dispatch(showNotification(result.error, 'error'));
            setLoading(false);
        }
    }

    const fetchCategoryProducts = async () => {
        try {
            setProductsLoading(true);
            const queryString = new URLSearchParams({
                categories: formData.name,
                pageSize: 1000 
            }).toString();
            const response = await productService.fetchProductList(queryString);
            setCategoryProducts(response.data.items || []);
            console.log('Все товары категории', response.data.items);
        } catch (error) {
            console.error('Ошибка при загрузке товаров категории:', error);
            dispatch(showNotification('Ошибка при загрузке товаров категории', 'error'));
        } finally {
            setProductsLoading(false);
        }
    };

    // Обработчики изменения полей формы
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value
        });
        
        // Очистка ошибки валидации при изменении поля
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: ''
            });
        }
    };

    // Обработчик загрузки изображения
    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Проверка расширения файла
        const allowedExtensions = ['.jpg', '.jpeg', '.png'];
        const fileExtension = '.' + file.name.split('.').pop().toLowerCase();
    
        if (!allowedExtensions.includes(fileExtension)) {
            dispatch(showNotification('Пожалуйста, выберите изображение в формате JPG, JPEG или PNG', 'error'));
            // Сбросить значение input file
            e.target.value = '';
            return;
        }

        // Проверка размера файла
        const maxSize = 10 * 1024 * 1024; // 10 МБ 
        if (file.size > maxSize) {
            dispatch(showNotification('Размер изображения не должен превышать 10 МБ', 'error'));
            // Сбросить значение input file
            e.target.value = '';
            return;
        }

        setImageFile(file);
            setImagePreview(URL.createObjectURL(file));
    };

    // Обработчик удаления изображения
    const handleRemoveImage = () => {
        // Удаляем изображение из состояния
        setImageFile(null);
        setImagePreview('');
        setFormData({
            ...formData,
            imagePath: ''
        });
    };

    // Валидация формы
    const validateForm = () => {
        const errors = {};
        
        const trimmedTitle = formData.title.trim();
        if (!formData.title || trimmedTitle.length < 2) {
            errors.title = 'Название категории должно содержать не менее 2 символов';
        } else if (trimmedTitle.length > 200) {
            errors.title = 'Название категории не должно превышать 200 символов';
        }

        // Проверка на уникальность названия
        if (categories) {
            const existingCategory = categories.find(
                c => c.title.toLowerCase() === trimmedTitle.toLowerCase() && c.id !== formData.id
            );
            
            if (existingCategory) {
                errors.title = 'Категория с таким названием уже существует';
            }
        }
        
        if (formData.description && formData.description.trim().length > 1000) {
            errors.description = 'Описание не должно превышать 1000 символов';
        }
        
        setValidationErrors(errors);
        return Object.keys(errors).length === 0;
    };

    // Добавление новой характеристики
    const handleAddCharacteristic = () => {
        const trimmedName = newCharacteristic.trim();
        if (trimmedName.length < 2) {
            setValidationErrors({
                ...validationErrors,
                newCharacteristic: 'Название характеристики должно содержать не менее 2 символов'
            });
            return;
        }

        if (trimmedName.length > 100) {
            setValidationErrors({
                ...validationErrors,
                newCharacteristic: 'Название характеристики должно содержать не более 100 символов'
            });
            return;
        }

        // Проверка на уникальность названия характеристики
        const isDuplicate = formData.categoryCharacteristics.some(
            char => char.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (isDuplicate) {
            setValidationErrors({
                ...validationErrors,
                newCharacteristic: 'Характеристика с таким названием уже добавлена'
            });
            return;
        }
        
        const updatedCharacteristics = [
            ...formData.categoryCharacteristics,
            {
                id: 0, // Для новых характеристик будет 0, сервер присвоит ID
                name: newCharacteristic.trim(),
                displayOrder: formData.categoryCharacteristics.length
            }
        ];
        
        setFormData({
            ...formData,
            categoryCharacteristics: updatedCharacteristics
        });
        
        setNewCharacteristic('');
        setValidationErrors({
            ...validationErrors,
            newCharacteristic: ''
        });
    };

    // Удаление характеристики
    const handleRemoveCharacteristic = (index) => {
        const updatedCharacteristics = [...formData.categoryCharacteristics];
        updatedCharacteristics.splice(index, 1);
        
        // Обновляем порядок отображения
        const reorderedCharacteristics = updatedCharacteristics.map((characteristic, idx) => ({
            ...characteristic,
            displayOrder: idx
        }));
        
        setFormData({
            ...formData,
            categoryCharacteristics: reorderedCharacteristics
        });
    };

    const handleDragStart = () => {
        setIsDragging(true);
    };

    const handleDragEnd = (result) => {
        setIsDragging(false);
        if (!result.destination) return;
        
        const items = Array.from(formData.categoryCharacteristics);
        const [reorderedItem] = items.splice(result.source.index, 1);
        items.splice(result.destination.index, 0, reorderedItem);
        
        const reorderedCharacteristics = items.map((characteristic, idx) => ({
            ...characteristic,
            displayOrder: idx
        }));
        
        setFormData({
            ...formData,
            categoryCharacteristics: reorderedCharacteristics
        });
    };

    // Функции для работы с товарами
    const handleProductDragEnd = (result) => {
        if (!result.destination) return;

        const newProducts = Array.from(categoryProducts);
        const [reorderedItem] = newProducts.splice(result.source.index, 1);
        newProducts.splice(result.destination.index, 0, reorderedItem);

        setCategoryProducts(newProducts);
        setHasProductsChanges(true);
    };

    const handleSaveProductsOrder = async () => {
        try {
            const orderUpdates = categoryProducts.map((product, index) => ({
                productId: product.id,
                displayOrder: index
            }));

            await productService.updateProductsOrder(orderUpdates);
            dispatch(showNotification('Порядок товаров успешно сохранен', 'success'));
            setHasProductsChanges(false);
        } catch (error) {
            console.error('Ошибка при сохранении порядка товаров:', error);
            dispatch(showNotification('Ошибка при сохранении порядка товаров', 'error'));
        }
    };

    const handleEditProduct = (productId) => {
        navigate(`/performance-admin-panel/products/${productId}`);
    };

    // Отправка формы
    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!validateForm()) {
            return;
        }

        setLoading(true);

        // Создаем FormData для отправки файла и данных формы
        const categoryData = new FormData();
        categoryData.append('title', formData.title);
        categoryData.append('description', formData.description);
        if (imageFile) {
            categoryData.append('image', imageFile);
        }
        if (formData.imagePath) {
            categoryData.append('imagePath', formData.imagePath);
        }
        // Добавляем характеристики
        categoryData.append('categoryCharacteristics', JSON.stringify(formData.categoryCharacteristics));
        
        let result;
        if (isNew) {
            result = await categoryService.createCategory(categoryData);
        } else {
            result = await categoryService.updateCategory(formData.id, categoryData);
        }

        if (result.success) {
            // Перезагружаем весь список категорий
            dispatch(fetchAllCategories());
            dispatch(showNotification(isNew ? 'Категория успешно создана' : 'Категория успешно обновлена', 'success'));
            navigate('/performance-admin-panel/categories');
            setLoading(false);
        }
        else {
            dispatch(showNotification(result.error, 'error'));
            setLoading(false);
        }
    };

    // Отмена и возврат к списку категорий
    const handleCancel = () => {
        navigate('/performance-admin-panel/categories');
    };

    // Функция для начала редактирования характеристики
    const handleStartEditing = (index, name) => {
        setEditingCharacteristicIndex(index);
        setEditingCharacteristicName(name);
    };

    // Функция для сохранения изменений характеристики
    const handleSaveCharacteristicEdit = () => {
        const trimmedName = editingCharacteristicName.trim();
        
        // Валидация длины
        if (trimmedName.length < 2) {
            setValidationErrors({
                ...validationErrors,
                editCharacteristic: 'Название характеристики должно содержать не менее 2 символов'
            });
            return;
        }

        if (trimmedName.length > 100) {
            setValidationErrors({
                ...validationErrors,
                editCharacteristic: 'Название характеристики должно содержать не более 100 символов'
            });
            return;
        }

        // Проверка на уникальность, исключая текущую редактируемую характеристику
        const isDuplicate = formData.categoryCharacteristics.some(
            (char, idx) => idx !== editingCharacteristicIndex && 
                        char.name.toLowerCase() === trimmedName.toLowerCase()
        );

        if (isDuplicate) {
            setValidationErrors({
                ...validationErrors,
                editCharacteristic: 'Характеристика с таким названием уже существует'
            });
            return;
        }

        // Обновляем характеристику
        const updatedCharacteristics = [...formData.categoryCharacteristics];
        updatedCharacteristics[editingCharacteristicIndex] = {
            ...updatedCharacteristics[editingCharacteristicIndex],
            name: trimmedName
        };
        
        setFormData({
            ...formData,
            categoryCharacteristics: updatedCharacteristics
        });
        
        // Сбрасываем состояние редактирования
        setEditingCharacteristicIndex(null);
        setEditingCharacteristicName('');
        setValidationErrors({
            ...validationErrors,
            editCharacteristic: ''
        });
    };

    // Функция для отмены редактирования характеристики
    const handleCancelEdit = () => {
        setEditingCharacteristicIndex(null);
        setEditingCharacteristicName('');
        setValidationErrors({
            ...validationErrors,
            editCharacteristic: ''
        });
    };

    return (
        <Container className="py-4"> 
            <h2 className="mb-4">{isNew ? 'Создание новой категории' : 'Редактирование категории'}</h2>
            {loading ? (
                <LoadingSpinner message={isNew ? 'Создание категории...' : 'Загрузка данных категории...'}  containerHeight="vh-50"/>
            ) : error ? (
                <div className="alert alert-danger">
                    {error}
                </div>
            ) : (
                <Form onSubmit={handleSubmit}>
                    <Row>
                        <Col md={8}>
                            <Form.Group className="mb-4">
                                <Form.Label>Название <span className="text-danger">*</span></Form.Label>
                                <Form.Control
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleChange}
                                    isInvalid={!!validationErrors.title}
                                    placeholder="Например: Электроника"
                                    required
                                />
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.title}
                                </Form.Control.Feedback>
                                {/* <Form.Text className="text-muted">
                                    От 2 до 200 символов.
                                </Form.Text> */}
                            </Form.Group>

                            <Form.Group className="mb-4">
                                <Form.Label>Описание</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={4}
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    isInvalid={!!validationErrors.description}
                                    placeholder="Краткое описание категории товаров"
                                />
                                <Form.Control.Feedback type="invalid">
                                    {validationErrors.description}
                                </Form.Control.Feedback>
                                {/* <Form.Text className="text-muted">
                                    Не более 1000 символов
                                </Form.Text> */}
                            </Form.Group>
                        </Col>
                        
                        <Col md={4}>
                            <Form.Group className="mb-4">
                                <Form.Label>Изображение</Form.Label>
                                <div className="d-flex flex-column align-items-center border rounded p-3">
                                    {imagePreview ? (
                                        <div className="mb-3 text-center position-relative">
                                            <img 
                                                src={imagePreview} 
                                                alt="Предпросмотр" 
                                                style={{ maxWidth: '100%', maxHeight: '200px' }} 
                                                className="img-thumbnail mb-2" 
                                            />
                                            <Button 
                                                variant="danger" 
                                                size="sm" 
                                                className="position-absolute top-0 end-0"
                                                onClick={handleRemoveImage}
                                                title="Удалить изображение"
                                            >
                                                <FaTimes />
                                            </Button>
                                        </div>
                                    ) : (
                                        <div className="text-center text-muted mb-3">
                                            <p>Изображение не выбрано</p>
                                        </div>
                                    )}
                                    <Form.Control
                                        type="file"
                                        accept=".jpg,.jpeg,.png"
                                        onChange={handleImageChange}
                                        className="mb-2"
                                    />
                                    <Form.Text className="text-muted text-center">
                                        Рекомендуемый размер: 800x600px
                                    </Form.Text>
                                </div>
                            </Form.Group>
                        </Col>
                    </Row>

                    {/* <hr className="my-4" /> */}

                    <h4 className="mb-3">Характеристики категории</h4>
                    <p className="text-muted">
                        Добавьте характеристики, которые будут использоваться для товаров данной категории.
                    </p>
                    
                    <div className="mb-4">
                        <div className="d-flex mb-3">
                            <Form.Control
                                type="text"
                                value={newCharacteristic}
                                onChange={(e) => setNewCharacteristic(e.target.value)}
                                placeholder="Название новой характеристики"
                                className="me-2"
                                isInvalid={!!validationErrors.newCharacteristic}
                            />
                            <Button
                                variant="outline-primary"
                                onClick={handleAddCharacteristic}
                                style={{ minWidth: '120px' }}
                            >
                                <FaPlus /> Добавить
                            </Button>
                        </div>
                        {validationErrors.newCharacteristic && (
                            <div className="text-danger small mb-3">
                                {validationErrors.newCharacteristic}
                            </div>
                        )}
                        {formData.categoryCharacteristics.length > 0 ? (
                            <DragDropContext onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
                                <Droppable droppableId="characteristics">
                                    {(provided) => (
                                        <Table 
                                            striped 
                                            bordered 
                                            hover 
                                            responsive
                                            ref={provided.innerRef}
                                            {...provided.droppableProps}
                                        >
                                            <thead>
                                                <tr>
                                                    <th style={{ width: '5%' }}></th>
                                                    <th style={{ width: '65%' }}>Название характеристики <span className="text-danger">*</span></th>
                                                    <th style={{ width: '30%' }}>Действия</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {formData.categoryCharacteristics.map((characteristic, index) => (
                                                    <Draggable 
                                                        key={`characteristic-${index}`} 
                                                        draggableId={`characteristic-${index}`} 
                                                        index={index}
                                                        isDragDisabled={editingCharacteristicIndex !== null}
                                                    >
                                                        {(provided, snapshot) => (
                                                            <tr
                                                                ref={provided.innerRef}
                                                                {...provided.draggableProps}
                                                                className={snapshot.isDragging ? 'dragging' : ''}
                                                            >
                                                                <td className="text-center">
                                                                    <div
                                                                        {...provided.dragHandleProps}
                                                                        className="drag-handle"
                                                                        style={{ cursor: 'grab' }}
                                                                    >
                                                                        <FaGripVertical />
                                                                    </div>
                                                                </td>
                                                                <td>
                                                                    {editingCharacteristicIndex === index ? (
                                                                        <Form.Control
                                                                            type="text"
                                                                            value={editingCharacteristicName}
                                                                            onChange={(e) => setEditingCharacteristicName(e.target.value)}
                                                                            isInvalid={!!validationErrors.editCharacteristic}
                                                                        />
                                                                    ) : (
                                                                        <div>
                                                                            {characteristic.name}
                                                                        </div>
                                                                    )}
                                                                    {editingCharacteristicIndex === index && validationErrors.editCharacteristic && (
                                                                        <div className="text-danger small mt-1">
                                                                            {validationErrors.editCharacteristic}
                                                                        </div>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <div className="d-flex justify-content-center">
                                                                        {editingCharacteristicIndex === index ? (
                                                                            <>
                                                                                <Button
                                                                                    variant="outline-success"
                                                                                    size="sm"
                                                                                    className="me-1"
                                                                                    onClick={handleSaveCharacteristicEdit}
                                                                                >
                                                                                    Сохранить
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-secondary"
                                                                                    size="sm"
                                                                                    onClick={handleCancelEdit}
                                                                                >
                                                                                    Отмена
                                                                                </Button>
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <Button
                                                                                    variant="outline-primary"
                                                                                    size="sm"
                                                                                    className="me-1"
                                                                                    onClick={() => handleStartEditing(index, characteristic.name)}
                                                                                    title="Редактировать"
                                                                                >
                                                                                    <FaPencilAlt />
                                                                                </Button>
                                                                                <Button
                                                                                    variant="outline-danger"
                                                                                    size="sm"
                                                                                    onClick={() => handleRemoveCharacteristic(index)}
                                                                                    title="Удалить"
                                                                                >
                                                                                    <FaTrash />
                                                                                </Button>
                                                                            </>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </Draggable>
                                                ))}
                                                {provided.placeholder}
                                            </tbody>
                                        </Table>
                                    )}
                                </Droppable>
                            </DragDropContext>
                        ) : (
                            <div className="alert alert-info">
                                Характеристики пока не добавлены
                            </div>
                        )}
                    </div> 

                    {/* Секция управления товарами */}
                    {!isNew && formData.name && (
                        <>
                            <div className="mb-4">
                                <div className="d-flex justify-content-between align-items-center mb-3">
                                    <h4>Товары категории ({categoryProducts.length})</h4>
                                    {hasProductsChanges && (
                                        <Button 
                                            variant="success" 
                                            onClick={handleSaveProductsOrder}
                                            size="sm"
                                        >
                                            Сохранить порядок товаров
                                        </Button>
                                    )}
                                </div>
                                {hasProductsChanges && (
                                    <div className="alert alert-warning mb-3">
                                        У вас есть несохраненные изменения в порядке товаров
                                    </div>
                                )}

                                {productsLoading ? (
                                    <div className="text-center py-4">
                                        <div className="spinner-border text-primary" role="status">
                                            <span className="visually-hidden">Загрузка товаров...</span>
                                        </div>
                                    </div>
                                ) : categoryProducts.length === 0 ? (
                                    <div className="alert alert-info">
                                        В этой категории пока нет товаров
                                    </div>
                                ) : (
                                    <DragDropContext onDragEnd={handleProductDragEnd}>
                                        <Droppable droppableId="products-list">
                                            {(provided) => (
                                                <div 
                                                    {...provided.droppableProps}
                                                    ref={provided.innerRef}
                                                    className="border rounded p-2"
                                                    style={{ 
                                                        maxHeight: '400px', 
                                                        overflowY: 'auto',
                                                        minHeight: '100px'
                                                    }}
                                                >
                                                    {categoryProducts.map((product, index) => (
                                                        <Draggable 
                                                            key={product.id} 
                                                            draggableId={product.id.toString()} 
                                                            index={index}
                                                        >
                                                            {(provided, snapshot) => (
                                                                <div
                                                                    ref={provided.innerRef}
                                                                    {...provided.draggableProps}
                                                                    className={`d-flex align-items-center p-3 mb-2 border rounded ${
                                                                        snapshot.isDragging ? 'bg-primary text-white' : 'bg-light'
                                                                    }`}
                                                                    style={{
                                                                        ...provided.draggableProps.style
                                                                    }}
                                                                >
                                                                    <div 
                                                                        {...provided.dragHandleProps}
                                                                        className="me-3"
                                                                        style={{ cursor: 'grab' }}
                                                                    >
                                                                        <FaGripVertical className="text-muted" />
                                                                    </div>
                                                                    
                                                                    <div className="me-3">
                                                                        <img 
                                                                            src={product.image || defaultProductImage}
                                                                            alt={product.title}
                                                                            style={{ width: "60px", height: "60px", objectFit: "contain" }}
                                                                            draggable="false"
                                                                            onDragStart={(e) => e.preventDefault()}
                                                                            onError={(e) => {
                                                                                e.target.onerror = null; // Предотвращает зацикливание
                                                                                e.target.src = defaultProductImage;
                                                                            }}
                                                                        />
                                                                    </div>
                                                                    
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-1">{product.title}</h6>
                                                                        <small className={snapshot.isDragging ? 'text-white-50' : 'text-muted'}>
                                                                            Артикул: {product.articles.join(', ')} | 
                                                                            {product.price ? ` Цена: ${product.price} ₽` : ' Цена не указана'}
                                                                        </small>
                                                                    </div>

                                                                    <div className="text-end d-flex align-items-center">
                                                                        <Button
                                                                            variant="outline-primary"
                                                                            size="sm"
                                                                            className="me-2"
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleEditProduct(product.id);
                                                                            }}
                                                                            title="Редактировать товар"
                                                                        >
                                                                            <FaEdit />
                                                                        </Button>
                                                                        <span className={`badge ${snapshot.isDragging ? 'bg-light text-dark' : 'bg-secondary'}`}>
                                                                            #{index + 1}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            )}
                                                        </Draggable>
                                                    ))}
                                                    {provided.placeholder}
                                                </div>
                                            )}
                                        </Droppable>
                                    </DragDropContext>
                                )}
                        
                                <small className="text-muted">
                                    Перетащите товары для изменения их порядка отображения в каталоге
                                </small>
                            </div>
                        </>
                    )}

                    <hr className="my-4" />

                    <div className="d-flex justify-content-center mt-4">
                        <Button
                            variant="secondary"
                            className="me-2 px-5"
                            onClick={handleCancel}
                        >
                            Отмена
                        </Button>
                        <Button
                            type="submit"
                            variant="primary"
                            className="px-5"
                            disabled={loading}
                        >
                            {isNew ? 'Создать' : 'Сохранить'}
                        </Button>
                    </div> 
                </Form>
            )}
        </Container>
    );
};

export default AdminCategoryEdit;